import { useEffect, useMemo, useRef, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { AutoBidNotOrderedMarket } from '@ebalance/proto/bffuser/v1/auto_bid_notice_model_pb'
import { ProcurementNeedMarket } from '@ebalance/proto/bffuser/v1/supply_demand_gap_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Flex,
  TabsComponent,
  VStack,
} from '@enechain/ecloud-designsystem'

import { AutoBidNotOrderedBanner } from '~/components/bidding/common/AutoBidNotOrderedBanner'
import { BidCsvImportExcludedBanner } from '~/components/bidding/common/BidCsvImportExcludedBanner'
import {
  useRegisteredIssuerOptions,
  useRegisteredIssuerSelection,
} from '~/components/bidding/common/IssuerSelector'
import { UnimportedBidBanner } from '~/components/bidding/common/UnimportedBidBanner'
import { UnknownIssuerBidBanner } from '~/components/bidding/common/UnknownIssuerBidBanner'
import { findIssuerOption } from '~/components/bidding/common/bidIssuer'
import {
  JepxMemberBidWarning,
  useJepxMemberBidWarning,
} from '~/components/common/JepxMemberBidWarning'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WorkflowPageLayout } from '~/components/common/WorkflowPageLayout'
import { useLayoutAutoBidNotOrderedNotices } from '~/components/layouts/MainLayout/SideMenuDotsContext'
import { tabTriggerFontSize } from '~/constants/tabStyle'
import { useBidImportStatus } from '~/hooks/bidding/useBidImportStatus'
import {
  convertListIntradayResponse,
  useListBiddingIntradayOrders,
} from '~/hooks/bidding/useListBiddingIntradayOrders'
import { useListTradableAreas } from '~/hooks/bidding/useListTradableAreas'
import { useProcurementNeedChartData } from '~/hooks/bidding/useProcurementNeedChartData'
import { useBidCsvImportSuccessToast } from '~/hooks/useBidCsvImportSuccessToast'
import { useBusinessAreaFilter } from '~/hooks/useBusinessAreaFilter'
import { useLocationStateNotice } from '~/hooks/useLocationStateNotice'
import { useNowByMinute } from '~/hooks/useNowByMinute'
import { usePersistedFilters } from '~/hooks/usePersistedFilters'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import { appToaster } from '~/libs/toaster'
import { JEPX_AREA_CODES } from '~/models/area'
import {
  type BidCsvImportIssuerCount,
  type BidCsvImportNotice,
  earliestBidCsvImportDate,
  hasBidCsvImportExcluded,
  readBidCsvImportAppliedNotice,
} from '~/models/bidCsvImportReturn'
import {
  isSameProtoDate,
  jstYmd,
  startOfTodayJst,
  toProtoDate,
} from '~/models/dateTime'
import { readSettlementDetailLandingDate } from '~/models/settlementDetailLanding'
import { GcCountdown } from '~/pages/IntradayBidding/components/GcCountdown'
import { pruneToSelectableAreas } from '~/pages/IntradayBidding/components/IntradayAreaMultiSelect'
import { IntradayDeliveryDateControl } from '~/pages/IntradayBidding/components/IntradayDeliveryDateControl'
import { IntradayFilterToolbar } from '~/pages/IntradayBidding/components/IntradayFilterToolbar'
import { IntradayNeedsReviewBanner } from '~/pages/IntradayBidding/components/IntradayNeedsReviewBanner'
import { BidCreateTab } from '~/pages/IntradayBidding/components/tabs/BidCreateTab'
import { BidHistoryTab } from '~/pages/IntradayBidding/components/tabs/BidHistoryTab'
import { ExecutionResultTab } from '~/pages/IntradayBidding/components/tabs/ExecutionResultTab'
import { MarketBoardTab } from '~/pages/IntradayBidding/components/tabs/MarketBoardTab'
import {
  DEFAULT_INTRADAY_PAGE_TAB,
  INTRADAY_PAGE_TABS,
} from '~/pages/IntradayBidding/constants/tabs'
import type { IntradayPageTabKey } from '~/pages/IntradayBidding/constants/tabs'
import { IntradayWithdrawNoticeProvider } from '~/pages/IntradayBidding/hooks/useIntradayWithdrawNotice'
import { useSendFailureNotice } from '~/pages/IntradayBidding/hooks/useSendFailureNotice'
import {
  acceptanceStartAt,
  isBeforeAcceptance,
} from '~/pages/IntradayBidding/models/acceptanceStart'
import { resolveIntradayAreaEmptyState } from '~/pages/IntradayBidding/models/areaEmptyState'
import type { BidListFilterLanding } from '~/pages/IntradayBidding/models/bidListFilters'
import { convertIntradayBidFromOrder } from '~/pages/IntradayBidding/models/convertIntradayBidsFromOrders'
import { nextGcForDeliveryDate } from '~/pages/IntradayBidding/models/deliveryDate'
import {
  type IntradayGlobalFilters,
  type IntradaySideFilter,
  deserializeIntradayGlobalFilters,
  resolveIntradayTargetDate,
  serializeIntradayGlobalFilters,
  toIntradayTargetDateFilter,
} from '~/pages/IntradayBidding/models/globalFilters'
import { intradayNeedsReviewCount } from '~/pages/IntradayBidding/models/intradayNeedsReviewCount'
import { isShownInBidHistory } from '~/pages/IntradayBidding/models/intradayTableStatus'
import { intradayUnknownIssuerDeliveryContractCodes } from '~/pages/IntradayBidding/models/unknownIssuerBid'

/** AC-BIN-102 全体フィルタの localStorage key (画面ごとに一意、 AC-SC-031)。 */
const INTRADAY_GLOBAL_FILTERS_STORAGE_KEY = 'ebalance-intraday-global-filters'

/** 時間前の入札一覧は発注主体区分で絞らないので、 取込完了のトーストに入れる別の区分の件数は無い */
const NO_OTHER_ISSUERS: readonly BidCsvImportIssuerCount[] = []

/** この画面が受け取る入札 CSV の種別 (AC-BCI-038)。 スポットの結果は受け取らない */
const readIntradayCsvImportNotice = (
  state: unknown,
): BidCsvImportNotice | undefined =>
  readBidCsvImportAppliedNotice(state, ['intraday'])

/** 清算画面の「詳細」から開いた時の着地の日 (AC-JST-009)。 スポット向けの state は受け取らない */
const readIntradaySettlementLandingDate = (state: unknown): Date | undefined =>
  readSettlementDetailLandingDate(state, 'intraday')

export const IntradayBiddingPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const memberBidDisabledReason = useJepxMemberBidWarning()
  // layout が 1 か所で取得した記録を読む (画面から取得の hook を呼ばない)。 市場での絞り込みは部品が持つ。
  const { notices: autoBidNotices } = useLayoutAutoBidNotOrderedNotices()

  // workflow-back-bar-readonly AC-05/AC-16: caseId は URL クエリパラメータ
  // `?workflowId=` から取得し、read-only 制御 (完了済み
  // ケースで注文作成を無効化) に使う (fail-closed の理由は useCurrentWorkflowCase
  // の JSDoc 参照)。
  const {
    caseId,
    wfCase,
    isReadonly,
    isDateLocked,
    lockedTargetDate,
    isTargetDateReady,
    isTargetDateUnavailable,
  } = useCurrentWorkflowCase()

  // ----- AC-BIN-102 全体フィルタ (受渡日 / エリア / 売買区分)、 4 タブ (入札追加・
  // 板発注・入札履歴・約定履歴) で共有し localStorage で永続化する (AC-SC-031)。 -----
  const { filters, updateFilters } = usePersistedFilters<IntradayGlobalFilters>(
    {
      storageKey: INTRADAY_GLOBAL_FILTERS_STORAGE_KEY,
      codec: {
        deserialize: deserializeIntradayGlobalFilters,
        serialize: serializeIntradayGlobalFilters,
      },
    },
  )

  // コマ境界・日付をまたいだら GC の対象や「当日」 が繰り上がるよう、 1 分ごとに
  // 更新される現在時刻を使う。 当日は分 tick では参照を変えず、 日付境界でだけ
  // 作り直す (受渡日を依存に持つ query / memo を毎分作り直さないため)。
  const now = useNowByMinute()
  const { year: todayYear, month: todayMonth, day: todayDay } = jstYmd(now)
  const today = useMemo(
    () => new Date(todayYear, todayMonth - 1, todayDay),
    [todayYear, todayMonth, todayDay],
  )

  // ----- 受渡日 (= 一覧 / チャート / 札作成が対象にする日) -----
  // ワークフローケース由来で開いた場合はケースの対象日に固定する (design
  // `#intraday-case-locked-hint`)。
  //
  // AC-WFW-006a Decision 1 (fail-closed): caseId が付いている間は wfCase 取得完了
  // (`lockedTargetDate` 確定) まで日付操作 UI を disabled のままにし、 取得完了後は
  // 画面既定値へフォールバックせず必ずケース対象日を表示する (`lockedTargetDate ??
  // 全体フィルタの受渡日` で導出。 取得中/失敗時は全体フィルタの受渡日〈既定=当日〉を
  // そのまま表示するが、 disabled のため実質操作不可)。 caseId 無しは従来どおり
  // ユーザー操作を全体フィルタへ反映する (当日を選んだ時は日付でなく「当日」 として
  // 持つ。 [[IntradayTargetDateFilter]] 参照)。
  const targetDate =
    lockedTargetDate ?? resolveIntradayTargetDate(filters.targetDate, today)
  const setManualTargetDate = (next: Date): void => {
    updateFilters((prev) => ({
      ...prev,
      targetDate: toIntradayTargetDateFilter(next),
    }))
  }

  // page 直下の一覧は入札履歴タブ専用 (= 入札追加タブは BidCreateTab が受渡日で
  // 絞った query を別に持つ)。 履歴は複数日を横断して見るのが目的で、 履歴側の日付フィルタ
  // (すべて + distinct 受渡日) もこの結果から作るため、 受渡日フィルタは掛けない。
  const { data, dataUpdatedAt, isLoading } = useListBiddingIntradayOrders()
  // AC-BIN-057 / 089a / 089b / 118: 札の送信が失敗に終わった時 (開場時の自動送信・提出や承認の
  // 後の送信・送信中の送り直し) に通知する。 どの sub-tab を見ていても届くよう page 直下で
  // polling の一覧を観測する。
  const withdrawnHereIds = useRef<Set<string>>(new Set())
  useSendFailureNotice(data?.items, withdrawnHereIds)

  // ページタブ: 入札追加 / 板発注 / 入札履歴 / 約定履歴 の 4 つ (サブタブは無い。 #9629)。
  // 入札 CSV を取り込んで移ってきた時は入札追加タブで開く (AC-BCI-038)。
  const [csvImportNotice, dismissCsvImportNotice] = useLocationStateNotice(
    readIntradayCsvImportNotice,
  )
  useBidCsvImportSuccessToast(csvImportNotice, NO_OTHER_ISSUERS)
  // 清算画面の「詳細」から移ってきた時は約定履歴タブで開く (AC-JST-009)。
  const [settlementLandingDate] = useLocationStateNotice(
    readIntradaySettlementLandingDate,
  )
  const [pageTab, setPageTab] = useState<IntradayPageTabKey>(
    settlementLandingDate !== undefined
      ? 'executionResult'
      : csvImportNotice === undefined
        ? DEFAULT_INTRADAY_PAGE_TAB
        : 'bidCreate',
  )

  // ----- 実効の受渡日 (= 画面に出す日 = 一覧・板・札作成が使う日) -----
  // 過去日を選べるのは過去の結果を見る入札履歴・約定履歴だけ (AC-BIN-102 / AC-BIN-000a)。
  // 入札追加・板発注は当日以降。 ヘッダーの下限・画面遷移時のリセット・タブへ渡す受渡日の
  // 3 つをこの判定 1 つから作る。
  const allowsPastDates =
    pageTab === 'executionResult' || pageTab === 'bidHistory'
  // 当日以降に限るタブで全体フィルタが過去日を持っている時は、 画面に出す日も板・札作成も
  // 当日に寄せる (ケース対象日の取得中・取得失敗の間や、 開いたまま日付が過去になった時。
  // ヘッダーと板が別の日を指さないよう、 ここで 1 回だけ決めて両方へ渡す)。 全体フィルタ
  // そのものを書き換えるのは画面遷移時のリセットだけ。 ケース対象日が確定していればその日を
  // 使う (過去のケースもそのまま見る)。
  const effectiveTargetDate =
    lockedTargetDate === undefined &&
    !allowsPastDates &&
    targetDate.getTime() < today.getTime()
      ? today
      : targetDate
  // 一括板取消 (AC-BIN-112) の結果は選択中の受渡日の札で数える ([[IntradayWithdrawNoticeProvider]])。
  // 受渡日の Date は描画ごとに作り直されうるので、 時刻の値で memo して観測の effect を空回りさせない。
  const effectiveTargetTime = effectiveTargetDate.getTime()
  const selectedTargetDateProto = useMemo(
    () => toProtoDate(new Date(effectiveTargetTime)),
    [effectiveTargetTime],
  )

  // 発注主体と登録不足理由は、画面・札作成が実際に使う同じ受渡日断面で解決する。
  // 保存済みの過去日を当日へ寄せる場合も、過去日の不足理由を混在させない (AC-BIN-111)。
  const { data: tradableAreasData } = useListTradableAreas(
    effectiveTargetDate,
    {
      enabled: isTargetDateReady,
    },
  )

  // AC-BIN-120: 発注主体区分の選択。
  // チャート・自社過不足の区分別表示 (AC-BIN-121) は #11176 で対応する。
  const [issuerType, setIssuerType] = useState(LicenseType.UNSPECIFIED)
  // 既定の区分 (選択肢の先頭 = 需要 BG ＞ 発電 ＞ 自己託送 BG) の選択と、 選択肢に無い区分を
  // 未選択に戻すのは画面で行う。 区分の部品は開閉式のカードの中にあって閉じている間は描かないので、
  // 部品に任せると開くまで既定が選ばれない (#12102 レビュー)。 カードの中は表示だけ (IssuerSelectorView)。
  // 動かすのは区分を使うタブ (入札追加・板発注) で選択肢が 1 件以上ある間だけ (main で区分の部品を
  // 描いていた条件と同じ)。 区分の取得中・取得失敗の間 (keepsValueUntilLoaded) と、 入札履歴・
  // 約定履歴で過去日を選んだ間は選択を保つ (AC-BIN-120「タブを切り替えても選択が保たれる」。
  // #12102 レビュー 2 回目)。 選択肢は登録済みの区分 (受渡日に有効なエリアが無い区分も残し、
  // エリアの空状態から選び直せる。 #12250 で上部バーの区分の部品が使っていたものと同じ)。
  const issuerOptions = useRegisteredIssuerOptions(effectiveTargetDate, {
    enabled: isTargetDateReady,
  })
  const usesIssuer = pageTab === 'bidCreate' || pageTab === 'marketBoard'
  useRegisteredIssuerSelection({
    deliveryDate: effectiveTargetDate,
    enabled: isTargetDateReady,
    value: issuerType,
    onChange: setIssuerType,
    isDisabled: !usesIssuer || issuerOptions.length === 0,
    selectsDefault: true,
    keepsValueUntilLoaded: true,
  })
  const selectedIssuer = findIssuerOption(issuerOptions, issuerType)
  const { selectableAreaCodes: businessAreas, isBusinessAreasResolved } =
    useBusinessAreaFilter(JEPX_AREA_CODES)
  const areaEmptyState = resolveIntradayAreaEmptyState({
    issuer: selectedIssuer,
    businessAreaCodes: isBusinessAreasResolved ? businessAreas : undefined,
    registrationIssues: tradableAreasData?.registrationIssues,
    jepxAreaCodes: JEPX_AREA_CODES,
  })

  // 初期 = 全エリア選択 (= design intraday.html の「すべて」)。 AC-BIN-018 で単一選択
  // radio → **複数選択** MultiSelect になったので、 以降は「全 9 エリア」・「単一エリア」・
  // 「複数エリア」 のいずれかを取り得る。 widget 上の全解除は [[IntradayAreaMultiSelect]] が
  // 全 9 エリアへ正規化するため、 全体の条件 ([[IntradayFilterToolbar]]) 経由でこの state が空集合に
  // なることはない。
  // 空集合は「0 件表示」。
  const selectedAreas = filters.selectedAreas
  const setSelectedAreas = (next: Set<AreaCode>): void => {
    updateFilters((prev) => ({ ...prev, selectedAreas: next }))
  }
  // 選択に残った業務エリア外のエリアを落とす ([[pruneToSelectableAreas]]。 localStorage から戻した分・
  // 業務エリア設定の変更で対象外になった分)。 エリアの部品は開閉式のカードの中にあって閉じている間は
  // 描かないので、 部品の整理だけに頼らず画面でも行う (#12102 レビュー。 閉じたまま業務エリア外で
  // 一覧・チャート・札作成の初期値を絞り続けないため)。
  useEffect(() => {
    const next = pruneToSelectableAreas(selectedAreas, businessAreas)
    if (next === undefined) return
    updateFilters((prev) => ({ ...prev, selectedAreas: next }))
  }, [selectedAreas, businessAreas, updateFilters])
  // 全体フィルタの売買区分 (AC-BIN-102)。 板発注は両側を常時表示する UI のため対象外。
  // 入札追加タブ (BidCreateTab の入札一覧) は AC-BIN-018 が「エリア・売/買は全体
  // フィルターの値をそのまま適用する」と定めるため、 約定履歴タブと同じくこの state を
  // そのまま渡す (ステータス絞り込みは入札追加タブ固有のため BidCreateTab のローカル
  // state のまま)。
  const side = filters.side
  const setSide = (next: IntradaySideFilter): void => {
    updateFilters((prev) => ({ ...prev, side: next }))
  }

  // 清算画面の「詳細」から移ってきた時、 全体フィルターを受渡日 = 清算書の日、 エリア・売買区分を
  // 「すべて」にする (AC-BIN-102 の追記。 清算書に含まれる取引が絞り込みで隠れないようにするため)。
  // 反映後の選択は保持され、 他の画面にも引き継がれる。 ワークフローで固定中は日付を変えない。
  // 契機は画面を開いた時の 1 回だけ (updateFilters は毎レンダー新しい参照になる)。
  useEffect(() => {
    if (settlementLandingDate === undefined) return
    updateFilters((prev) => ({
      targetDate: isDateLocked
        ? prev.targetDate
        : toIntradayTargetDateFilter(settlementLandingDate),
      selectedAreas: new Set(JEPX_AREA_CODES),
      side: 'all',
    }))
    // 契機は画面を開いた時の 1 回だけ (上記)。
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 上記理由
  }, [])

  // 入札 CSV を取り込んで移ってきた時、 全体フィルターを取り込んだ札を含むように広げる (AC-BCI-038)。
  // 受渡日は取り込んだ受渡日のうち最も早い日 (ワークフローで固定中は変えない)、 エリアは今の選択に
  // 足し (選択を狭めない)、 売買区分は単一選択なので取り込んだ札の売買が今の選択に含まれない時は
  // 「すべて」にする。 入札一覧の絞り込み (ステータス・コマ・ワークフロー) は下の bidListFilterLanding で広げる。
  // 契機は画面を開いた時の 1 回だけ (updateFilters は毎レンダー新しい参照になる)。
  const csvImportFiltersAppliedRef = useRef(false)
  // 着地で決めた受渡日。 同じ mount で下の過去日リセットが更新前の値 (復元した過去日) で
  // 判定して、 着地の日を「当日」 で上書きしないよう、 判定に使わせる。
  const csvImportLandingDateRef = useRef<
    IntradayGlobalFilters['targetDate'] | undefined
  >(undefined)
  useEffect(() => {
    if (csvImportNotice === undefined || csvImportFiltersAppliedRef.current) {
      return
    }
    csvImportFiltersAppliedRef.current = true
    const earliest = isDateLocked
      ? undefined
      : earliestBidCsvImportDate(csvImportNotice)
    if (earliest !== undefined) {
      csvImportLandingDateRef.current = toIntradayTargetDateFilter(earliest)
    }
    updateFilters((prev) => ({
      ...prev,
      targetDate:
        earliest === undefined
          ? prev.targetDate
          : toIntradayTargetDateFilter(earliest),
      selectedAreas: new Set([...prev.selectedAreas, ...csvImportNotice.areas]),
      side:
        prev.side !== 'all' &&
        csvImportNotice.sides.some((importedSide) => importedSide !== prev.side)
          ? 'all'
          : prev.side,
    }))
    // 契機は画面を開いた時の 1 回だけ (上記)。
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 上記理由
  }, [])
  // 取り込んだ札のエリアに業務エリア外があれば、 全体フィルターのエリアを「すべて」 にする (AC-BCI-038)。
  // 時間前の CSV 取込は業務エリアを検査しないので、 業務エリア外の札も取り込まれる。 業務エリア外は
  // エリアの選択肢に無く、 上で選択に足しても業務エリアの整理 ([[pruneToSelectableAreas]]。 選択肢に無いエリアを外す)
  // が外すため、 取り込んだ札が一覧に出ない。 「すべて」 は整理の対象外なので残る。
  // - 業務エリアの取得前は業務エリア外か判定できない (取得中は全 9 エリアを返す) ので、 取得後に見る
  // - 取得した同じ回に整理が取得前の選択から外した集合を書くことがあるので、 「すべて」 になったのを
  //   見届けてから終える (それまでは選択が変わるたびに見直す)。 終えた後に利用者が絞るのは止めない
  const csvImportOutsideAreasDoneRef = useRef(false)
  useEffect(() => {
    if (
      csvImportNotice === undefined ||
      csvImportOutsideAreasDoneRef.current ||
      !isBusinessAreasResolved
    ) {
      return
    }
    const hasOutside = csvImportNotice.areas.some(
      (area) => !businessAreas.includes(area),
    )
    const isAll = JEPX_AREA_CODES.every((code) =>
      filters.selectedAreas.has(code),
    )
    if (!hasOutside || isAll) {
      csvImportOutsideAreasDoneRef.current = true
      return
    }
    updateFilters((prev) => ({
      ...prev,
      selectedAreas: new Set(JEPX_AREA_CODES),
    }))
    // updateFilters は毎レンダー新しい参照になる (上の着地と同じ)。 終わりは ref で 1 回だけ。
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 上記理由
  }, [
    csvImportNotice,
    isBusinessAreasResolved,
    businessAreas,
    filters.selectedAreas,
  ])

  // 入札一覧の絞り込みを広げる契機 (CSV 取込からの着地 AC-BCI-038・ワークフローから開いた時)。
  // 入札追加タブはタブの切り替えで作り直されるので、 「画面を開いて 1 回だけ」 はここで持つ。
  // ワークフローから開いた時は、 札が持つワークフローの業務ID がケースの取得で決まるまで渡さない。
  const [bidListFilterLandingApplied, setBidListFilterLandingApplied] =
    useState(false)
  const openedWorkflowId =
    caseId === undefined || wfCase === undefined || wfCase.workflowId === ''
      ? undefined
      : wfCase.workflowId
  const bidListFilterLanding = useMemo<BidListFilterLanding | undefined>(() => {
    if (bidListFilterLandingApplied) return undefined
    if (caseId !== undefined && wfCase === undefined) return undefined
    if (csvImportNotice !== undefined) {
      return {
        kind: 'csvImport',
        komas: csvImportNotice.komas ?? [],
        workflowId: openedWorkflowId,
      }
    }
    return openedWorkflowId === undefined
      ? undefined
      : { kind: 'workflow', workflowId: openedWorkflowId }
  }, [
    bidListFilterLandingApplied,
    caseId,
    wfCase,
    csvImportNotice,
    openedWorkflowId,
  ])

  const rows = useMemo(
    () => (data !== undefined ? convertListIntradayResponse(data) : []),
    [data],
  )

  // ----- 調達必要量 (need) の実データ (= GetProcurementNeed、 時間前市場 = スポット後残差) -----
  // 時間前市場は当日 (受渡日) を対象にする。 チェック中エリアをコマ別合算して基準線にする
  // (設計書 bidding-position-need.md §7 採番、恒久 spec 未反映)。取得エラー時は need line
  // 非表示 + 注記、cover/executed は描画継続 (= 設計書同節、ADR bidding-position-need-
  // bilateral-baseline.md 残論点参照)。
  // caseId 付きで開いている間、 ケース対象日が確定するまでは対象日依存のこの
  // need query も取得を開始しない (AC-WFW-006a Decision 1)。
  const { needByKoma, needUnavailable } = useProcurementNeedChartData(
    effectiveTargetDate,
    selectedAreas,
    ProcurementNeedMarket.INTRADAY,
    undefined,
    { enabled: isTargetDateReady },
  )

  // 入札履歴に出す row。 受渡日・エリア・売/買はどれも全体フィルター (AC-BIN-024 / AC-BIN-102)
  // の値で絞る。 受渡日は過去日も選べる (allowsPastDates) ので effectiveTargetDate をそのまま使う。
  // 終わった札と送信中の札だけを出す (AC-BIN-024 / §5。 [[isShownInBidHistory]])。
  const historyRows = useMemo(() => {
    const target = toProtoDate(effectiveTargetDate)
    return rows.filter((r) => {
      if (!isShownInBidHistory(convertIntradayBidFromOrder(r))) return false
      if (!isSameProtoDate(r.targetDate, target)) return false
      if (!selectedAreas.has(r.areaCode)) return false
      if (side === 'buy' && r.direction !== Side.BUY) return false
      if (side === 'sell' && r.direction !== Side.SELL) return false
      return true
    })
  }, [rows, effectiveTargetDate, selectedAreas, side])

  // 「次にカウントダウン対象とする GC 時刻」 + 「対象コマ」。 受渡日により変わる
  // (当日 = 現在コマの次の GC / 未来日 = その日の最初の GC / 過去日 = GC 済)。
  const { gcAt: nextGcAt, targetKoma } = useMemo(
    () => nextGcForDeliveryDate(now, effectiveTargetDate),
    [now, effectiveTargetDate],
  )
  // AC-BIN-089c / AC-BIN-001: 受付前 (受渡日前日 12:00 JST より前) は札を作れない
  // ので、 次の GC ではなく受付開始までの残時間を数える。
  const acceptanceOpenAt = useMemo(
    () =>
      isBeforeAcceptance(now, effectiveTargetDate)
        ? acceptanceStartAt(effectiveTargetDate)
        : undefined,
    [now, effectiveTargetDate],
  )

  // AC-BIN-102: 入札追加・板発注は受渡日に過去日を選べない (AC-BIN-000a)。
  // 約定履歴・入札履歴で過去日を選んだ後にこれらのタブへ遷移した場合、 受渡日を
  // 当日にリセットしその旨をトーストで通知する (エリア・売買区分は保持する)。
  // ワークフローケースで開いている間は、 ケースの取得中も含めて対象外 (受渡日は
  // ケース対象日に固定するため。 取得中は lockedTargetDate が未確定なので isDateLocked
  // で見る)。
  //
  // 契機は画面 (pageTab) の遷移と初回表示 (localStorage から過去日を復元した
  // 場合) だけに限る。 分 tick の now や受渡日の変化を契機にすると、 入札追加・板発注を
  // 開いたまま 0 時を跨いだだけで、 過去日を選んでいない利用者にもリセットのトーストが
  // 出る。 当日を選んでいる状態は日付でなく「当日」 として持つので、 翌日以降の再訪や
  // 0 時跨ぎでは過去日にならない。 「どの画面で判定済みか」 は実行回数ではなく画面キーの
  // 値で持つ (StrictMode の effect 二重実行でも 2 回目は同じキーなので何もしない)。
  const pastDateCheckedTabRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (isDateLocked) return
    if (pastDateCheckedTabRef.current === pageTab) return
    pastDateCheckedTabRef.current = pageTab
    if (allowsPastDates) return
    const selected = csvImportLandingDateRef.current ?? filters.targetDate
    csvImportLandingDateRef.current = undefined
    if (selected === 'today') return
    if (selected.getTime() >= startOfTodayJst().getTime()) return
    updateFilters((prev) => ({ ...prev, targetDate: 'today' }))
    appToaster.create({
      type: 'info',
      // 入札追加は main 既存の文言 (deliveryDate)、 板発注は全体フィルタ共有用の文言。
      title:
        pageTab === 'marketBoard'
          ? t('page:biddingIntraday.globalFilter.pastDateResetToast')
          : t('page:biddingIntraday.deliveryDate.pastDateResetToast'),
    })
    // 契機は画面の遷移 (pageTab) とケース固定の解除だけ (上記)。 受渡日・t・
    // updateFilters (usePersistedFilters が毎レンダー新しい参照を返す) は契機ではない。
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 上記理由
  }, [isDateLocked, pageTab])

  // 初回取得中はページの枠 (ヘッダー・受渡日) を残し、本文だけを RegionLoading で覆う。
  const isInitialLoading = isLoading && data === undefined

  // GC カウントダウンを出すタブ (入札追加・入札履歴)。 全体の条件のバーではなくページヘッダー右端に置く
  // (AC-BIN-131 ※ / AC-BIN-001)
  const showsGcCountdown = pageTab === 'bidCreate' || pageTab === 'bidHistory'
  // 全体フィルター (エリア・売/買) は板発注を除く 3 タブ共通の 1 本のバーに置く (AC-BIN-102 / AC-BIN-131)
  const showsGlobalFilters = pageTab !== 'marketBoard'

  const showsBiddingWarnings =
    pageTab === 'bidCreate' || pageTab === 'marketBoard'
  const unknownIssuerDeliveryContractCodes = useMemo(
    () => intradayUnknownIssuerDeliveryContractCodes(rows, effectiveTargetDate),
    [rows, effectiveTargetDate],
  )

  const needsReviewCount = useMemo(
    () => intradayNeedsReviewCount(rows, effectiveTargetDate),
    [rows, effectiveTargetDate],
  )

  const hasUnimportedBids = useBidImportStatus(effectiveTargetDate, true, {
    enabled: isTargetDateReady,
  })

  const breadcrumbItems = [
    { label: t('page:biddingIntraday.breadcrumb.bidding') },
    { label: t('page:biddingIntraday.breadcrumb.page'), bold: true },
  ]

  return (
    <WorkflowPageLayout
      breadcrumbItems={breadcrumbItems}
      // GC カウントダウン (入札追加・入札履歴)。 design `intraday.html` の `#gc-countdown-header` と
      // 同じくページヘッダーの右端に置く。 対象コマは GcCountdown バッジ内の `.gc-slot` スロットが持つ
      // (issue #4858)。 全コマ GC 済 (= 過去日 / 当日 22:30 以降) は対象コマが無いので undefined を渡し、
      // バッジ側が「GC済」 だけを出す。
      headerExtra={
        showsGcCountdown ? (
          <GcCountdown
            acceptanceOpenAt={acceptanceOpenAt}
            gcAt={nextGcAt}
            targetKoma={targetKoma}
          />
        ) : undefined
      }
    >
      {/* 縦スクロール + 左右 padding (16px) は親 SideMenuLayoutContentLayout の content Box
        (overflowY:auto / padding:4) が既に提供する。 ここで overflowY:auto を二重に持つと
        内側 Flex の scrollbar (padding 無し) がコンテンツ右端に被って見切れるため持たない
        (= design intraday.html `<main overflow-y:auto>` 単一スクロールと同方針)。 */}
      {/* AC-BIN-008b: 取消のリトライ失敗確定 (PENDING → FAILED) を polling で観測した時に
        1 回だけ通知する。 どのタブを見ていても届くよう本体の直上に置き、 WITHDRAW 応答
        (BidCreateTab / 詳細モーダル) と同じ観測状態を共有する。 一括板取消 (AC-BIN-112) の
        結果トーストも同じ一覧の観測で出す (取得時刻 dataUpdatedAt で受付前の一覧を除く)。
        WorkflowPageLayout の children 側に置くのは、 ワークフローが見つからない時 (AC-WFW-040) に本体ごと外して
        通知も出さないため。 */}
      <IntradayWithdrawNoticeProvider
        orders={data?.items}
        ordersUpdatedAt={dataUpdatedAt}
        selectedTargetDate={selectedTargetDateProto}
        withdrawnHereIds={withdrawnHereIds}
      >
        <RegionLoading minHeight="200px" isLoading={isInitialLoading}>
          {isInitialLoading ? null : (
            <Flex flexDirection="column" gap={3}>
              {showsBiddingWarnings &&
                memberBidDisabledReason !== undefined &&
                memberBidDisabledReason !== '' && (
                  <JepxMemberBidWarning reason={memberBidDisabledReason} />
                )}
              {/* 自動入札が発注しなかったコマの警告バナー (AC-BIN-117)。 「入札追加」 と 「板発注」 だけに
            出し (入札履歴・約定履歴では出さない)、 受渡日・エリアの選択で絞らないので画面の最上部
            (取引可能エリアのバナーより上) に置く。 */}
              {showsBiddingWarnings && (
                <AutoBidNotOrderedBanner
                  market={AutoBidNotOrderedMarket.INTRADAY}
                  notices={autoBidNotices}
                />
              )}
              {showsBiddingWarnings && (
                <UnknownIssuerBidBanner
                  deliveryContractCodes={unknownIssuerDeliveryContractCodes}
                />
              )}
              {pageTab === 'bidCreate' && (
                <IntradayNeedsReviewBanner count={needsReviewCount} />
              )}
              {showsBiddingWarnings && (
                <UnimportedBidBanner hasUnimportedBids={hasUnimportedBids} />
              )}

              {/* 入札 CSV の確定で対象外にした行があった時のエラーバナー (AC-BCI-038)。
                全部取り込めた時は成功のトースト。 上の警告バナーより下・タブの上に置く。 */}
              {csvImportNotice !== undefined &&
                hasBidCsvImportExcluded(csvImportNotice) && (
                  <BidCsvImportExcludedBanner
                    notice={csvImportNotice}
                    onDismiss={dismissCsvImportNotice}
                  />
                )}

              {/* 時間前入札の開閉式フィルター (4 タブ共通の 1 本。 画面の最上部・ページタブの上)。
                  受渡日は全タブ、 発注主体は入札追加・板発注、 エリア・売/買 は板発注を除く 3 タブ
                  (AC-BIN-131 / AC-BIN-103)。 どれも条件のカードの中で選ぶ。 GC カウントダウンはこのバーに
                  含めずページヘッダー右端に出す (AC-BIN-131 ※ / AC-BIN-001)。 */}
              <IntradayFilterToolbar
                deliveryDate={{
                  date: effectiveTargetDate,
                  control: (
                    <IntradayDeliveryDateControl
                      allowPastDates={allowsPastDates}
                      isDateLocked={isDateLocked}
                      isTargetDateConfirmed={lockedTargetDate !== undefined}
                      onTargetDateChange={setManualTargetDate}
                      targetDate={effectiveTargetDate}
                    />
                  ),
                }}
                global={
                  showsGlobalFilters
                    ? {
                        selectedAreas,
                        onAreasChange: setSelectedAreas,
                        side,
                        onSideChange: setSide,
                      }
                    : undefined
                }
                issuer={
                  pageTab === 'bidCreate' || pageTab === 'marketBoard'
                    ? {
                        value: issuerType,
                        onChange: setIssuerType,
                        options: issuerOptions,
                        selected: selectedIssuer,
                      }
                    : undefined
                }
              />

              {/* ページタブ (= design の cross-page tab を in-app tab で表現)。 */}
              <TabsComponent
                items={INTRADAY_PAGE_TABS.map((def) => ({
                  key: def.key,
                  label: t(def.labelKey),
                }))}
                onSelect={(key) => setPageTab(key)}
                selected={pageTab}
                tabSx={{ fontSize: tabTriggerFontSize.raw }}
              />

              {/* ===== 本文 ===== */}
              {pageTab === 'bidCreate' && isTargetDateUnavailable && (
                <Alert
                  role="alert"
                  status="error"
                  title={t('page:biddingIntraday.market.board.fetchFailed')}
                />
              )}
              {pageTab === 'bidCreate' && !isTargetDateUnavailable && (
                <VStack align="stretch" gap={3} minHeight="600px">
                  <BidCreateTab
                    areaEmptyState={areaEmptyState}
                    isReadonly={isReadonly}
                    isTargetDateReady={isTargetDateReady}
                    listFilterLanding={bidListFilterLanding}
                    memberBidDisabledReason={memberBidDisabledReason}
                    needByKoma={needByKoma}
                    needUnavailable={needUnavailable}
                    onListFilterLandingApplied={() =>
                      setBidListFilterLandingApplied(true)
                    }
                    openedWorkflowId={openedWorkflowId}
                    selectedAreas={selectedAreas}
                    side={side}
                    targetDate={effectiveTargetDate}
                  />
                </VStack>
              )}

              {pageTab === 'bidHistory' && (
                <VStack align="stretch" gap={3} minHeight="400px">
                  <BidHistoryTab isLoading={isLoading} rows={historyRows} />
                </VStack>
              )}

              {pageTab === 'executionResult' && (
                <VStack align="stretch" gap={3} minHeight="400px">
                  <ExecutionResultTab
                    selectedAreas={selectedAreas}
                    side={side}
                    targetDate={effectiveTargetDate}
                  />
                </VStack>
              )}

              {/* AC-WFW-006a ※: ケース対象日が決まるまで板発注タブを mount しない。 板・約定・
                  連系線・自社入札・need の取得と stream、 コマ選択の復元はどれも受渡日に依存し、
                  mount したままでは既定日 (当日) で取りに行く (#11142)。 対象日を得られない時は
                  取得を止めたままの空の板を「板なし」 に見せないよう、 取得失敗として出す。 */}
              {pageTab === 'marketBoard' && isTargetDateUnavailable && (
                <Alert
                  role="alert"
                  status="error"
                  title={t('page:biddingIntraday.market.board.fetchFailed')}
                />
              )}
              {pageTab === 'marketBoard' &&
                !isTargetDateReady &&
                !isTargetDateUnavailable && (
                  <RegionLoading minHeight="600px" isLoading />
                )}
              {pageTab === 'marketBoard' && isTargetDateReady && (
                <MarketBoardTab
                  isReadonly={isReadonly}
                  lockedDeliveryDate={lockedTargetDate}
                  memberBidDisabledReason={memberBidDisabledReason}
                  targetDate={effectiveTargetDate}
                />
              )}
            </Flex>
          )}
        </RegionLoading>
      </IntradayWithdrawNoticeProvider>
    </WorkflowPageLayout>
  )
}
